import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import type { SpiffProcessStatus } from "@/api/dto/process-instance";
import { fetchProcessInstance } from "@/api/endpoints/fetch-process-instance";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import { epochToDate } from "@/domain/case/map-process-instance";

/** 案件を特定するのに必要な最小の情報。 */
export type CaseRef = {
  readonly caseId: number;
  readonly processModelId: string;
};

export type CaseDetail = {
  readonly caseId: number;
  readonly rawStatus: SpiffProcessStatus;
  readonly processModelId: string;
  readonly processModelDisplayName: string;
  readonly initiator: string | null;
  readonly startedAt: Date | null;
  readonly endedAt: Date | null;
  /** process instance metadata を key→value の辞書にしたもの。 */
  readonly metadata: Readonly<Record<string, string>>;
};

export type CaseHistoryEvent = {
  readonly id: number;
  readonly eventType: string;
  /** タスクの表示名（BPMN の name）。 */
  readonly taskName: string | null;
  /** BPMN の id。表示名はモデルごとに違うので、判定はこちらで行う。 */
  readonly taskIdentifier: string | null;
  readonly actor: string | null;
  readonly occurredAt: Date | null;
};

type SpiffCaseDetailResponse = {
  readonly id: number;
  readonly status: SpiffProcessStatus;
  readonly process_model_identifier: string;
  readonly process_model_display_name: string;
  readonly process_initiator_username?: string | null;
  readonly start_in_seconds?: number | null;
  readonly end_in_seconds?: number | null;
  readonly process_metadata?: readonly {
    readonly key: string;
    readonly value: string;
  }[];
};

type SpiffLogResponse = {
  readonly results?: readonly {
    readonly id: number;
    readonly event_type: string;
    readonly task_definition_name?: string | null;
    readonly task_definition_identifier?: string | null;
    /** SpiffWorkflow のタスク種別（内部タスクの判別に使う）。 */
    readonly bpmn_task_type?: string | null;
    readonly username?: string | null;
    /** epoch 秒の文字列（小数を含む）。 */
    readonly timestamp?: string | number | null;
  }[];
};

/**
 * 案件詳細。
 *
 * 取得は `for-me` を先に叩き、拒否されたら管理者経路に落とす
 * （`fetch-process-instance.ts`）。全案件モニタから自分が関与していない案件を
 * 開くと `for-me` は 400 になるため。
 */
export function caseDetailQueryOptions(client: ApiClient, ref: CaseRef) {
  const modifiedId = toModifiedProcessModelId(ref.processModelId);

  const fetchDetail = async (): Promise<CaseDetail> => {
    const row = await fetchProcessInstance<SpiffCaseDetailResponse>(
      client,
      modifiedId,
      ref.caseId,
    );

    return {
      caseId: row.id,
      rawStatus: row.status,
      processModelId: row.process_model_identifier,
      processModelDisplayName: row.process_model_display_name,
      initiator: row.process_initiator_username ?? null,
      startedAt: epochToDate(row.start_in_seconds),
      endedAt: epochToDate(row.end_in_seconds),
      metadata: Object.fromEntries(
        (row.process_metadata ?? []).map((entry) => [entry.key, entry.value]),
      ),
    };
  };

  return queryOptions({
    queryKey: qk.processInstances.detail(modifiedId, ref.caseId),
    queryFn: fetchDetail,
    staleTime: 30_000,
  });
}

/** 案件の履歴（process instance event）。新しい順に並べて返す。 */
export function caseHistoryQueryOptions(client: ApiClient, ref: CaseRef) {
  const modifiedId = toModifiedProcessModelId(ref.processModelId);

  const fetchHistory = async (): Promise<readonly CaseHistoryEvent[]> => {
    const response = await client.get<SpiffLogResponse>(
      `/logs/${modifiedId}/${ref.caseId}`,
      // events=true でないと「タスク完了」1 件しか返らない（監査には全イベントが必要）
      { query: { per_page: 100, page: 1, events: true } },
    );

    const events: CaseHistoryEvent[] = (response.results ?? [])
      .filter((row) => !isInternalTask(row.bpmn_task_type))
      .map((row) => ({
        id: row.id,
        eventType: row.event_type,
        // 名前が無い実要素は id で出す（黙って消すと履歴が欠ける）
        taskName:
          row.task_definition_name ?? row.task_definition_identifier ?? null,
        taskIdentifier: row.task_definition_identifier ?? null,
        actor: row.username ?? null,
        occurredAt: epochToDate(toEpochSeconds(row.timestamp)),
      }));

    // 新しい順（id の降順）。イベント id は単調増加するため timestamp より安定。
    return events.sort((a, b) => b.id - a.id);
  };

  return queryOptions({
    queryKey: [
      ...qk.processInstances.detail(modifiedId, ref.caseId),
      "history",
    ],
    queryFn: fetchHistory,
    staleTime: 30_000,
  });
}

/** logs の timestamp は小数付きの文字列で来る。 */
function toEpochSeconds(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) ? Math.floor(seconds) : null;
}

/**
 * 案件の詳細。ref が null の間は問い合わせない（モデル id が分かる前に呼ぶ画面が
 * あるため。図の取得 useCaseDiagram と同じ扱い）。
 */
export function useCaseDetail(ref: CaseRef | null): UseQueryResult<CaseDetail> {
  const client = useApiClient();
  return useQuery({
    ...caseDetailQueryOptions(client, ref ?? { caseId: 0, processModelId: "" }),
    enabled: ref !== null,
  });
}

export function useCaseHistory(
  ref: CaseRef,
): UseQueryResult<readonly CaseHistoryEvent[]> {
  return useQuery(caseHistoryQueryOptions(useApiClient(), ref));
}

/**
 * BPMN に描かれていない SpiffWorkflow の内部タスク。
 *
 * ルートの `Start` / `End` と、並行分岐を畳む `EndJoin`。名前を持たないので画面では
 * 「—」の行になり、履歴が読めなくなる。業務の履歴として意味を持たないので落とす。
 */
const INTERNAL_TASK_TYPES = new Set([
  "BpmnStartTask",
  "SimpleBpmnTask",
  "_EndJoin",
  "_BoundaryEventParent",
]);

function isInternalTask(bpmnTaskType: string | null | undefined): boolean {
  return bpmnTaskType !== null && bpmnTaskType !== undefined
    ? INTERNAL_TASK_TYPES.has(bpmnTaskType)
    : false;
}
